import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Checkbox,
  Chip,
  InputAdornment,
  TextField,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import PeopleIcon from "@material-ui/icons/People";
import ListAltIcon from "@material-ui/icons/ListAlt";
import SendIcon from "@material-ui/icons/Send";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GroupIcon from "@material-ui/icons/Group";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { i18n } from "../../translate/i18n";
import CampaignProModal from "../../components/CampaignProModal";
import OfficialCampaignModal from "../../components/OfficialCampaignModal";

const t = (key, fallback, opts) => {
  const result = i18n.t(`groupManagement.${key}`, opts);
  if (result === `groupManagement.${key}` || result === key) return fallback || key;
  return result;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0, 2),
    minHeight: 0,
  },
  backButton: { marginRight: theme.spacing(1) },
  card: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2.5, 3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  cardHeaderIcon: {
    fontSize: 32,
    opacity: 0.95,
  },
  cardHeaderTextWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  cardHeaderTitle: {
    fontWeight: 700,
    fontSize: "1.3rem",
    lineHeight: 1.2,
  },
  cardHeaderSubtitle: {
    fontWeight: 400,
    fontSize: "0.85rem",
    opacity: 0.85,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  section: {
    flex: 1,
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "auto",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    flexShrink: 0,
    flexWrap: "wrap",
  },
  searchField: {
    flex: 1,
    minWidth: 200,
    maxWidth: 400,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "0.875rem",
    },
  },
  countBadge: {
    borderRadius: 20,
    padding: theme.spacing(0.5, 1.5),
    fontWeight: 600,
    fontSize: "0.8rem",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  selectedBadge: {
    borderRadius: 20,
    padding: theme.spacing(0.5, 1.5),
    fontWeight: 600,
    fontSize: "0.8rem",
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  toolbarActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(0.8, 2),
    fontWeight: 600,
    fontSize: "0.8rem",
  },
  tableWrap: {
    flex: 1,
    minHeight: 0,
    borderRadius: 8,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    ...theme.scrollbarStyles,
  },
  table: { minWidth: 400 },
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5, 2),
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      backgroundColor: theme.palette.grey[50],
      borderBottom: `2px solid ${theme.palette.divider}`,
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
    transition: "background-color 0.15s ease",
  },
  tableCell: {
    padding: theme.spacing(1.2, 2),
    fontSize: "0.875rem",
  },
  chipSuperAdmin: {
    backgroundColor: "#f57c00",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 24,
  },
  chipAdmin: {
    backgroundColor: "#43a047",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 24,
  },
  chipMember: {
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 24,
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  emptyStateIcon: {
    fontSize: 56,
    color: theme.palette.grey[300],
    marginBottom: theme.spacing(2),
  },
  progressDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      minWidth: 380,
      padding: theme.spacing(1),
    },
  },
  progressHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  progressStats: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  progressBarWrap: {
    borderRadius: 6,
    overflow: "hidden",
    height: 8,
    backgroundColor: theme.palette.grey[200],
  },
  campaignDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      padding: theme.spacing(1),
    },
  },
  campaignBtn: {
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1.2, 2),
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
}));

/* -------- SSE buffer parser (unchanged) -------- */
function processSSEBuffer(bufferRef, chunk, onEvent) {
  bufferRef.current += chunk.replace(/\r\n/g, "\n");
  const idx = bufferRef.current.lastIndexOf("\n\n");
  if (idx === -1) return;
  const complete = bufferRef.current.slice(0, idx);
  bufferRef.current = bufferRef.current.slice(idx + 2);
  complete.split("\n\n").forEach((block) => {
    const line = block.split("\n").find((l) => l.startsWith("data:"));
    if (line) {
      try {
        onEvent(JSON.parse(line.slice(5).trim()));
      } catch (_) {}
    }
  });
}

const GroupParticipants = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { groupId } = useParams();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [participants, setParticipants] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: "" });
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [chooseCampaignModalOpen, setChooseCampaignModalOpen] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [campaignModalType, setCampaignModalType] = useState(null);
  const [createdListId, setCreatedListId] = useState(null);
  const abortRef = useRef(null);
  const sseBufferRef = useRef("");

  /* -------- Data fetching (SSE streaming - unchanged logic) -------- */
  useEffect(() => {
    if (!connectionId || !groupId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroupInfo = async () => {
      try {
        const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}`, {
          params: { connectionId },
        });
        setGroupName(data?.subject || "");
      } catch (_) {
        setGroupName("");
      }
    };
    fetchGroupInfo();

    const baseURL = api.defaults.baseURL || "";
    const streamUrl = `${baseURL}/group-management/groups/${encodeURIComponent(groupId)}/participants/stream?connectionId=${connectionId}`;

    const fetchParticipantsStream = async () => {
      setProgress({ current: 0, total: 0, message: t("normalizingProgress", "Resolvendo nomes e números dos contatos. Isso pode levar até 1 minuto.") });
      const ac = new AbortController();
      abortRef.current = ac;
      sseBufferRef.current = "";
      try {
        const res = await fetch(streamUrl, { credentials: "include", signal: ac.signal });
        if (!res.ok || !res.body) {
          const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
            params: { connectionId },
          });
          setParticipants(Array.isArray(data) ? data : []);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          processSSEBuffer(sseBufferRef, chunk, (data) => {
            if (data.type === "progress") {
              setProgress({ current: data.current || 0, total: data.total || 1, message: data.message || "" });
            } else if (data.type === "result") {
              setParticipants(Array.isArray(data.participants) ? data.participants : []);
            } else if (data.type === "error") {
              toastError({ response: { data: { message: data.message } } });
            }
          });
        }
        const rest = sseBufferRef.current.trim();
        if (rest) {
          const line = rest.split("\n").find((l) => l.startsWith("data:"));
          if (line) {
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.type === "result") setParticipants(Array.isArray(data.participants) ? data.participants : []);
            } catch (_) {}
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        try {
          const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
            params: { connectionId },
          });
          setParticipants(Array.isArray(data) ? data : []);
        } catch (e) {
          toastError(e);
        }
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    };

    fetchParticipantsStream();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [connectionId, groupId, history]);

  /* -------- Filtering -------- */
  const filteredParticipants = useMemo(() => {
    if (!searchTerm.trim()) return participants;
    const term = searchTerm.toLowerCase().trim();
    return participants.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const number = (p.number || p.id || "").toLowerCase();
      return name.includes(term) || number.includes(term);
    });
  }, [participants, searchTerm]);

  /* -------- Selection -------- */
  const getKey = useCallback((p) => (p.number || p.id || "").toString().replace(/\D/g, ""), []);

  const toggleSelectAll = () => {
    const visible = filteredParticipants;
    const allVisibleKeys = visible.map(getKey).filter(Boolean);
    const allSelected = allVisibleKeys.length > 0 && allVisibleKeys.every((k) => selectedNumbers.has(k));
    if (allSelected) {
      setSelectedNumbers((prev) => {
        const next = new Set(prev);
        allVisibleKeys.forEach((k) => next.delete(k));
        return next;
      });
    } else {
      setSelectedNumbers((prev) => {
        const next = new Set(prev);
        allVisibleKeys.forEach((k) => next.add(k));
        return next;
      });
    }
  };

  const toggleSelect = (num) => {
    const key = (num || "").toString().replace(/\D/g, "");
    if (!key) return;
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isSelected = (p) => selectedNumbers.has(getKey(p));

  const visibleSelectedCount = useMemo(
    () => filteredParticipants.filter((p) => isSelected(p)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredParticipants, selectedNumbers]
  );

  const allVisibleSelected = useMemo(
    () => filteredParticipants.length > 0 && visibleSelectedCount === filteredParticipants.length,
    [filteredParticipants, visibleSelectedCount]
  );

  /* -------- Campaign generation -------- */
  const handleGerarCampanha = () => {
    if (selectedNumbers.size === 0) {
      toast.error(t("selectAtLeastOneParticipant", "Selecione ao menos um participante."));
      return;
    }
    setChooseCampaignModalOpen(true);
  };

  const createListAndOpenCampaign = async (type) => {
    setChooseCampaignModalOpen(false);
    const selected = participants.filter((p) => isSelected(p));
    if (selected.length === 0) return;
    setCreatingList(true);
    try {
      const baseName = groupName.trim() || t("listFromGroup", "Lista do Grupo");
      const listName = `${baseName} ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
      const { data: list } = await api.post("/contact-lists-pro", {
        name: listName,
        allowDuplicates: false,
        optIn: true,
      });
      const listId = list.id;
      const BATCH = 10;
      for (let i = 0; i < selected.length; i += BATCH) {
        const batch = selected.slice(i, i + BATCH);
        await Promise.all(
          batch.map((p) =>
            api.post(`/contact-lists-pro/${listId}/items`, {
              number: getKey(p),
              name: p.name || null,
            })
          )
        );
      }
      setCreatedListId(listId);
      setCampaignModalType(type);
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingList(false);
    }
  };

  const closeCampaignModal = () => {
    setCampaignModalType(null);
    setCreatedListId(null);
  };

  /* -------- CSV Export -------- */
  const exportCsv = () => {
    const headers = [
      t("participantName", "Nome"),
      t("participantNumber", "Número"),
      t("admin", "Admin"),
    ];
    const rows = participants.map((p) => [
      (p.name || "").replace(/"/g, '""'),
      p.number || "",
      p.isAdmin || p.isSuperAdmin ? "Sim" : "Não",
    ]);
    const csv = [headers.join(";"), ...rows.map((r) => r.map((c) => `"${c}"`).join(";"))].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `participantes-grupo-${groupId.replace(/@g\.us$/, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!connectionId || !groupId) return null;

  const progressPercent =
    progress.total > 0 ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : undefined;

  /* -------- Role chip renderer -------- */
  const renderRoleChip = (p) => {
    if (p.isSuperAdmin) {
      return (
        <Chip
          label={t("superadmin", "Superadmin")}
          className={classes.chipSuperAdmin}
          size="small"
        />
      );
    }
    if (p.isAdmin) {
      return (
        <Chip
          label={t("admin", "Admin")}
          className={classes.chipAdmin}
          size="small"
        />
      );
    }
    return (
      <Chip
        label={t("member", "Membro")}
        variant="outlined"
        className={classes.chipMember}
        size="small"
      />
    );
  };

  return (
    <MainContainer fullWidth>
      <MainHeader>
        <Button
          className={classes.backButton}
          onClick={() => history.push(`/group-management/groups?connectionId=${connectionId}`)}
          startIcon={<ArrowBackIcon />}
        >
          {t("back", "Voltar")}
        </Button>
        <Title>{t("participants", "Participantes")}</Title>
      </MainHeader>

      {/* Campaign type chooser dialog */}
      <Dialog
        open={chooseCampaignModalOpen}
        onClose={() => setChooseCampaignModalOpen(false)}
        maxWidth="xs"
        fullWidth
        className={classes.campaignDialog}
      >
        <DialogTitle>{t("chooseCampaignType", "Escolha o tipo de campanha")}</DialogTitle>
        <DialogContent style={{ paddingBottom: 24 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
            {t("chooseCampaignTypeHint", "Será criada uma lista em Campanhas PRO com os contatos selecionados.")}
          </Typography>
          <Box display="flex" flexDirection="column">
            <Button
              variant="contained"
              color="primary"
              onClick={() => createListAndOpenCampaign("pro")}
              fullWidth
              className={classes.campaignBtn}
            >
              {t("campaignPro", "Campanha PRO")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => createListAndOpenCampaign("official")}
              fullWidth
              className={classes.campaignBtn}
            >
              {t("campaignOfficial", "Campanha Oficial")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Creating list dialog */}
      {creatingList && (
        <Dialog open disableEscapeKeyDown className={classes.progressDialog}>
          <DialogTitle>{t("creatingList", "Criando lista...")}</DialogTitle>
          <DialogContent>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">
                {t("creatingListHint", "Adicionando contatos à lista. Aguarde.")}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Campaign modals */}
      {campaignModalType === "pro" && createdListId && (
        <CampaignProModal
          open={true}
          onClose={closeCampaignModal}
          initialContactListProId={createdListId}
          resetPagination={() => {}}
        />
      )}
      {campaignModalType === "official" && createdListId && (
        <OfficialCampaignModal
          open={true}
          onClose={closeCampaignModal}
          onSuccess={closeCampaignModal}
          initialContactListProId={createdListId}
        />
      )}

      {/* Loading / progress dialog */}
      <Dialog open={loading} disableEscapeKeyDown className={classes.progressDialog}>
        <DialogTitle disableTypography>
          <Box className={classes.progressHeader}>
            <CircularProgress size={24} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {t("normalizingTitle", "Normalizando participantes")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {progress.total > 0 && progressPercent !== undefined && (
            <>
              <Box className={classes.progressStats}>
                <Typography variant="body2" color="textSecondary">
                  {t("validatingContactCount", "Validando contato {{current}} de {{total}}", {
                    current: progress.current,
                    total: progress.total,
                  })}
                </Typography>
                <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
                  {progressPercent}%
                </Typography>
              </Box>
              <Box className={classes.progressBarWrap}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  style={{ height: 8, borderRadius: 6 }}
                />
              </Box>
            </>
          )}
          {progress.total > 0 && progressPercent === undefined && (
            <>
              <Typography variant="body2" color="primary" style={{ marginBottom: 8 }}>
                {progress.current} / {progress.total}
              </Typography>
              <LinearProgress variant="indeterminate" style={{ height: 6, borderRadius: 6 }} />
            </>
          )}
          {(!progress.total || progress.total === 0) && (
            <LinearProgress variant="indeterminate" style={{ height: 6, borderRadius: 6 }} />
          )}
          <Typography color="textSecondary" variant="body2" style={{ marginTop: 16 }}>
            {progress.message || t("normalizingProgress", "Resolvendo nomes e números dos contatos.")}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Main content */}
      <Box className={classes.root}>
        <Paper className={classes.card}>
          {/* Colored header with group name */}
          <Box className={classes.cardHeader}>
            <GroupIcon className={classes.cardHeaderIcon} />
            <Box className={classes.cardHeaderTextWrap}>
              <Typography className={classes.cardHeaderTitle}>
                {t("participantList", "Lista de participantes")}
              </Typography>
              {groupName && (
                <Typography className={classes.cardHeaderSubtitle}>
                  {groupName}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.section}>
            {/* Toolbar: search + badges + action buttons */}
            <Box className={classes.toolbarRow}>
              <TextField
                className={classes.searchField}
                variant="outlined"
                size="small"
                placeholder={t("searchGroups", "Buscar participante...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {!loading && (
                <Box className={classes.countBadge}>
                  <PeopleIcon style={{ fontSize: 16 }} />
                  {participants.length} {t("participants", "participantes")}
                </Box>
              )}

              {selectedNumbers.size > 0 && (
                <Box className={classes.selectedBadge}>
                  <CheckBoxIcon style={{ fontSize: 16 }} />
                  {selectedNumbers.size} {t("selected", selectedNumbers.size === 1 ? "selecionado" : "selecionados")}
                </Box>
              )}

              <Box className={classes.toolbarActions}>
                <Tooltip title={t("generateCampaign", "Gerar campanha")}>
                  <span>
                    <Button
                      className={classes.actionBtn}
                      startIcon={<SendIcon />}
                      onClick={handleGerarCampanha}
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={loading || participants.length === 0 || selectedNumbers.size === 0}
                    >
                      {t("generateCampaign", "Gerar campanha")}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={t("exportCsv", "Exportar CSV")}>
                  <span>
                    <Button
                      className={classes.actionBtn}
                      startIcon={<GetAppIcon />}
                      onClick={exportCsv}
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={loading || participants.length === 0}
                    >
                      {t("exportCsv", "Exportar CSV")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Table */}
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={4}>
                <CircularProgress />
              </Box>
            ) : filteredParticipants.length === 0 && participants.length === 0 ? (
              /* Empty state */
              <Box className={classes.emptyState}>
                <PersonOutlineIcon className={classes.emptyStateIcon} />
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                  {t("noParticipants", "Nenhum participante.")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("normalizingProgress", "Os participantes aparecerão aqui após o carregamento.")}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} className={classes.tableWrap}>
                <Table className={classes.table} size="medium" stickyHeader>
                  <TableHead>
                    <TableRow className={classes.tableHead}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
                          checked={allVisibleSelected}
                          onChange={toggleSelectAll}
                          color="primary"
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                        />
                      </TableCell>
                      <TableCell>{t("participantName", "Nome")}</TableCell>
                      <TableCell>{t("participantNumber", "Número")}</TableCell>
                      <TableCell align="center">{t("role", "Perfil")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredParticipants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className={classes.emptyState}>
                          <Typography variant="body2">
                            {t("noParticipants", "Nenhum participante encontrado.")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredParticipants.map((p) => (
                        <TableRow key={p.id || p.number} className={classes.tableRow}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected(p)}
                              onChange={() => toggleSelect(p.number || p.id)}
                              color="primary"
                              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                            />
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {p.name || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {p.number || p.id || "-"}
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {renderRoleChip(p)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupParticipants;
